import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ForwardedRef,
  type MouseEvent,
  type ReactNode,
  useEffect,
} from 'react';
import type {SelectorOption} from '../lib/selector-option';
import {ToggleGroup, ToggleGroupItem} from './ToggleGroup';

const getValueArray = <T extends string | number | boolean>(value?: T | T[]): T[] => {
  if (value !== undefined && value !== null) {
    return Array.isArray(value) ? value : [value];
  }

  return [];
};

function getToggleContent(
  label: string | ReactNode,
  disabled?: boolean,
  readOnly?: boolean,
  active?: boolean,
) {
  if (isValidElement(label)) {
    return cloneElement(label, {
      disabled,
      readOnly,
      active,
    });
  }

  return label;
}

export type TogglesProps<
  T extends string | number | boolean,
  M extends boolean | undefined = undefined,
> = {
  options: SelectorOption<T>[];
  onClick?: (
    event: MouseEvent<HTMLButtonElement>,
    value: SelectorOption<T>,
  ) => void | {shouldPrevent: boolean};
  className?: string;
  groupClassName?: string;
  onAnySelectorActive?: (isActive: boolean) => void;
  variant?: 'default' | 'tab';
  size?: 'default' | 'sm' | 'lg' | 'tab';
  theme?: 'default' | 'sky-blue';
  minSelected?: number;
  loading?: boolean;
  disabled?: boolean;
  disabledItems?: SelectorOption<T>['value'][];
  readonlyItems?: SelectorOption<T>['value'][];
  readOnly?: boolean;
  label?: string;
  multiple?: M;
} & (M extends true
  ? {
      value?: T[];
      onChange?: (value: T[], event: MouseEvent<HTMLButtonElement>) => void;
    }
  : {
      value?: T;
      onChange?: (value: T, event: MouseEvent<HTMLButtonElement>) => void;
    });

function TogglesInternal<
  T extends string | number | boolean,
  M extends boolean | undefined = undefined,
>(
  {
    className,
    groupClassName,
    loading,
    disabled,
    value,
    options,
    variant = 'default',
    size,
    theme = 'default',
    onAnySelectorActive,
    onClick,
    onChange,
    readOnly,
    minSelected = 0,
    disabledItems,
    readonlyItems,
    label,
    multiple = true,
  }: TogglesProps<T, M>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const handleValueChange = (newValue: string | string[]) => {
    if (!onChange) return;

    if (multiple) {
      const newValueArray = Array.isArray(newValue) ? newValue : [newValue];
      const typedValues = newValueArray.map(item => {
        const option = options.find(opt => String(opt.value) === item);
        return option ? option.value : (item as T);
      });

      (onChange as (value: T[], event: MouseEvent<HTMLButtonElement>) => void)?.(
        typedValues,
        {} as MouseEvent<HTMLButtonElement>,
      );
      return;
    }

    const singleValue = Array.isArray(newValue) ? newValue[0] : newValue;
    const option = options.find(opt => String(opt.value) === singleValue);
    const typedValue = option ? option.value : (singleValue as T);
    (onChange as (value: T, event: MouseEvent<HTMLButtonElement>) => void)?.(
      typedValue,
      {} as MouseEvent<HTMLButtonElement>,
    );
  };

  const handleItemClick =
    (option: SelectorOption<T>) => (event: MouseEvent<HTMLButtonElement>) => {
      if (disabled || disabledItems?.includes(option.value) || option.disabled) {
        event.preventDefault();
        return;
      }

      const result = onClick?.(event, option);

      if (result?.shouldPrevent) {
        event.preventDefault();
        return;
      }

      const valueArray = getValueArray(value);

      if (multiple) {
        const hasBeenSelected = valueArray.includes(option.value);
        const newSelected = hasBeenSelected
          ? valueArray.filter(selectedValue => selectedValue !== option.value)
          : [...valueArray, option.value];

        (onChange as (value: T[], event: MouseEvent<HTMLButtonElement>) => void)?.(
          newSelected,
          event,
        );
      } else {
        (onChange as (value: T, event: MouseEvent<HTMLButtonElement>) => void)?.(
          option.value,
          event,
        );
      }
    };

  const isAnyActive = getValueArray(value).length > 0;
  useEffect(() => {
    onAnySelectorActive?.(isAnyActive);
  }, [isAnyActive, onAnySelectorActive]);

  const currentValue = getValueArray(value).map(item => String(item));

  const toggleGroupProps = {
    variant,
    size: size ?? (variant === 'tab' ? 'tab' : 'default'),
    theme,
    onValueChange: handleValueChange,
    ...(multiple
      ? {type: 'multiple' as const, value: currentValue}
      : {type: 'single' as const, value: currentValue[0] ?? ''}),
  };

  return (
    <div ref={ref} className={className}>
      {label && (
        <div className="mb-2">
          <div className="select-none text-base font-normal text-[var(--toggles-label-text)]">
            {label}
          </div>
        </div>
      )}
      <ToggleGroup className={groupClassName} {...toggleGroupProps}>
        {options.map((option, index) => {
          const isSelected = Boolean(
            getValueArray(value).find(selectedValue => selectedValue === option.value),
          );
          const isDisabled =
            disabled || disabledItems?.includes(option.value) || option.disabled;
          const isMinSelected = getValueArray(value).length <= minSelected;
          const isItemReadOnly =
            readOnly ||
            (isMinSelected && isSelected) ||
            readonlyItems?.includes(option.value);

          return (
            <ToggleGroupItem
              key={index}
              value={String(option.value)}
              disabled={isDisabled || isItemReadOnly || loading}
              onClick={handleItemClick(option)}
            >
              {getToggleContent(option.label, isDisabled, isItemReadOnly, isSelected)}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </div>
  );
}

export type TogglesForwardType = <
  T extends string | number | boolean,
  M extends boolean | undefined = true,
>(
  props: TogglesProps<T, M> & {
    ref?: ForwardedRef<HTMLDivElement>;
  },
) => ReturnType<typeof TogglesInternal>;

export const Toggles = forwardRef(TogglesInternal) as TogglesForwardType;
